/**
 * Billing → Subscription (Előfizetés) section.
 * Reads GET /payments/subscription and offers Stripe Checkout / Customer
 * Portal redirects. Rendered in the Billing panel modal (App.tsx).
 */
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../auth/AuthProvider";
import { useToast } from "./Toast";
import { API_BASE } from "../config";

type SubscriptionInfo = {
  effective_plan: string;
  plan: string | null;
  status: string;
  trial_until: number;
  current_period_end: number;
  cancel_at_period_end: boolean;
};

export const SubscriptionSection: React.FC<{
  /** Set right after a ?billing=success return from Stripe Checkout. */
  refetchAfterCheckout?: boolean;
}> = ({ refetchAfterCheckout = false }) => {
  const toast = useToast();
  const { t } = useTranslation();
  const { ensureToken } = useAuth();
  const [sub, setSub] = useState<SubscriptionInfo | null>(null);
  const [busy, setBusy] = useState(false);

  // Latest sub for the delayed-refetch timers (avoids stale closures).
  const subRef = useRef<SubscriptionInfo | null>(null);
  subRef.current = sub;

  const load = async () => {
    try {
      const token = await ensureToken();
      const r = await fetch(`${API_BASE}/payments/subscription`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(`${r.status}`);
      setSub(await r.json());
    } catch (e) {
      console.error("[settings] subscription fetch failed", e);
      toast.error(t("settings.subLoadFailed"));
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Webhook race after a Stripe success redirect: the browser lands back
  // BEFORE the checkout.session.completed webhook flips the Subscriptions
  // item, so the mount fetch can still show trial/none. Refetch at ~2s and
  // ~5s until the paid state appears (load() itself toasts on failure — no
  // silent path). Deliberately just two timers, not a polling framework.
  useEffect(() => {
    if (!refetchAfterCheckout) return;
    const isPaid = () =>
      !!(subRef.current?.plan && ["active", "past_due"].includes(subRef.current.status));
    const timers = [2000, 5000].map((ms) =>
      setTimeout(() => {
        if (!isPaid()) load();
      }, ms)
    );
    return () => timers.forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refetchAfterCheckout]);

  const post = async (path: string, body?: object, failKey = "settings.subCheckoutFailed") => {
    if (busy) return;
    setBusy(true);
    try {
      const token = await ensureToken();
      const r = await fetch(`${API_BASE}${path}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: body ? JSON.stringify(body) : undefined,
      });
      if (r.status === 409 && (await r.json()).error === "already_subscribed") {
        // Server-side double-billing guard fired: the panel is showing stale
        // state (checkout completed but the webhook hadn't flipped the item
        // when we loaded). Refetch — the paid view hides the Subscribe buttons.
        toast.info(t("settings.subAlreadySubscribed"));
        await load();
        setBusy(false);
        return;
      }
      if (!r.ok) throw new Error(`${r.status}`);
      const { url } = await r.json();
      window.location.assign(url);
    } catch (e) {
      console.error("[settings] payments redirect failed", e);
      toast.error(t(failKey));
      setBusy(false);
    }
  };

  const planLabel =
    sub?.effective_plan === "trial" ? t("settings.subPlanTrial")
    : sub?.effective_plan === "alap" ? t("settings.subPlanAlap")
    : sub?.effective_plan === "pro" ? t("settings.subPlanPro")
    : t("settings.subPlanNone");
  const fmt = (unix: number) => new Date(unix * 1000).toLocaleDateString();

  const btnStyle: React.CSSProperties = {
    padding: "8px 14px",
    background: "var(--color-bg)",
    border: "1px solid var(--color-border)",
    borderRadius: 4,
    fontSize: 13,
    cursor: busy ? "not-allowed" : "pointer",
    color: "var(--color-ink)",
    opacity: busy ? 0.6 : 1,
  };

  return (
    <div>
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: "var(--color-title)" }}>
        {t("settings.subscription")}
      </div>
      {sub && (
        <>
          <div style={{ fontSize: 13, color: "var(--color-ink)", marginBottom: 6 }}>{planLabel}</div>
          {sub.effective_plan === "trial" && sub.trial_until > 0 && (
            <div style={{ fontSize: 12, color: "var(--color-muted)", marginBottom: 6 }}>
              {t("settings.subTrialEnds")}: {fmt(sub.trial_until)}
            </div>
          )}
          {sub.plan && sub.current_period_end > 0 && (
            <div style={{ fontSize: 12, color: "var(--color-muted)", marginBottom: 6 }}>
              {sub.cancel_at_period_end ? t("settings.subCancelAtEnd") : t("settings.subRenews")}
              {": "}
              {fmt(sub.current_period_end)}
            </div>
          )}
          <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
            {/* Checkout only when NO LIVE paid subscription exists — a second
                Checkout for an active/past_due subscriber would create a second
                Stripe subscription (double billing); plan changes for paying
                users go through the Customer Portal. Keyed on status, not plan
                presence: a fully CANCELED subscriber keeps plan set but must be
                able to re-subscribe via Checkout (the Portal can't start a new
                subscription when none is active). */}
            {!(sub.plan && ["active", "past_due"].includes(sub.status)) && (
              <>
                <button style={btnStyle} disabled={busy} onClick={() => post("/payments/checkout", { tier: "alap" })}>
                  {t("settings.subUpgradeAlap")}
                </button>
                <button style={btnStyle} disabled={busy} onClick={() => post("/payments/checkout", { tier: "pro" })}>
                  {t("settings.subUpgradePro")}
                </button>
              </>
            )}
            {sub.plan && (
              <button
                style={btnStyle}
                disabled={busy}
                onClick={() => post("/payments/portal", undefined, "settings.subPortalFailed")}
              >
                {t("settings.subManage")}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default SubscriptionSection;
